import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="gap-2 dark:text-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain rounded-lg" />
              <span className="font-heading text-primary-900 dark:text-slate-100 font-bold">KAI</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="font-heading text-3xl md:text-4xl text-primary-900 dark:text-slate-100 mb-2">Polityka Prywatności</h1>
          <p className="text-muted-foreground mb-8">Ostatnia aktualizacja: 7 lutego 2026 r.</p>
          
          <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed">
            
            {/* Wstęp */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">1. Informacje Ogólne</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Niniejsza Polityka Prywatności określa zasady przetwarzania i ochrony danych osobowych Użytkowników serwisu KAI (Kurdynowski AI).</li>
                <li><strong>Administratorem danych osobowych</strong> jest Biuro Rachunkowe Kurdynowski, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895, właściciel i operator serwisu KAI.</li>
                <li>Kontakt z Administratorem: <strong>crm@kurdynowski.pl</strong></li>
                <li>Administrator dokłada szczególnej staranności w celu ochrony interesów osób, których dane dotyczą, a w szczególności zapewnia, że zbierane przez niego dane są przetwarzane zgodnie z prawem.</li>
              </ol>
            </section>

            {/* Podstawa prawna */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">2. Podstawa Prawna Przetwarzania Danych</h2>
              <p className="mb-4">Dane osobowe są przetwarzane zgodnie z:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Rozporządzeniem Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. (RODO),</li>
                <li>Ustawą z dnia 10 maja 2018 r. o ochronie danych osobowych,</li>
                <li>Ustawą z dnia 18 lipca 2002 r. o świadczeniu usług drogą elektroniczną.</li>
              </ul>
            </section>

            {/* Zakres danych */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">3. Zakres Zbieranych Danych</h2>
              <p className="mb-4">W ramach korzystania z Serwisu KAI zbieramy następujące dane:</p>
              
              <h3 className="font-semibold text-primary-800 mt-4 mb-2">3.1. Dane podawane przy rejestracji:</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>imię i nazwisko,</li>
                <li>adres e-mail,</li>
                <li>hasło (przechowywane w formie zaszyfrowanej).</li>
              </ul>

              <h3 className="font-semibold text-primary-800 mt-4 mb-2">3.2. Dane do fakturowania (opcjonalnie):</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>nazwa firmy,</li>
                <li>NIP,</li>
                <li>adres siedziby.</li>
              </ul>

              <h3 className="font-semibold text-primary-800 mt-4 mb-2">3.3. Dane zbierane automatycznie:</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>adres IP,</li>
                <li>typ przeglądarki i systemu operacyjnego,</li>
                <li>data i czas korzystania z Serwisu,</li>
                <li>historia konwersacji z asystentem KAI.</li>
              </ul>
            </section>

            {/* Cele przetwarzania */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">4. Cele Przetwarzania Danych</h2>
              <p className="mb-4">Dane osobowe Użytkowników przetwarzane są w celu:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Świadczenia usług</strong> – na podstawie art. 6 ust. 1 lit. b RODO (wykonanie umowy):
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>utworzenia i obsługi Konta Użytkownika,</li>
                    <li>świadczenia usług asystenta KAI,</li>
                    <li>obsługi subskrypcji i płatności,</li>
                    <li>wystawiania faktur VAT.</li>
                  </ul>
                </li>
                <li><strong>Realizacji obowiązków prawnych</strong> – na podstawie art. 6 ust. 1 lit. c RODO:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>prowadzenia dokumentacji księgowej,</li>
                    <li>rozpatrywania reklamacji.</li>
                  </ul>
                </li>
                <li><strong>Prawnie uzasadnionych interesów Administratora</strong> – na podstawie art. 6 ust. 1 lit. f RODO:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>zapewnienia bezpieczeństwa Serwisu,</li>
                    <li>analizy i ulepszania jakości usług,</li>
                    <li>dochodzenia lub obrony przed roszczeniami.</li>
                  </ul>
                </li>
              </ol>
            </section>

            {/* Okres przechowywania */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">5. Okres Przechowywania Danych</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Dane konta:</strong> przez okres posiadania konta oraz do 30 dni po jego usunięciu.</li>
                <li><strong>Dane transakcyjne i faktury:</strong> przez okres wymagany przepisami prawa podatkowego (5 lat od końca roku kalendarzowego).</li>
                <li><strong>Historia konwersacji:</strong> przez okres posiadania konta lub do momentu jej usunięcia przez Użytkownika.</li>
                <li><strong>Dane analityczne:</strong> przez okres 26 miesięcy (zgodnie z praktyką Google Analytics).</li>
              </ul>
            </section>

            {/* Odbiorcy danych */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">6. Odbiorcy Danych</h2>
              <p className="mb-4">Dane osobowe mogą być przekazywane następującym kategoriom odbiorców:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Operatorzy płatności:</strong> Tpay, Stripe – w celu realizacji płatności.</li>
                <li><strong>Dostawcy usług AI:</strong> OpenAI, Anthropic, Google – w celu świadczenia usług asystenta (dane są anonimizowane w maksymalnym możliwym zakresie).</li>
                <li><strong>Dostawcy usług hostingowych i infrastruktury IT.</strong></li>
                <li><strong>Organy państwowe</strong> – na podstawie obowiązujących przepisów prawa.</li>
              </ul>
            </section>

            {/* Przekazywanie danych poza EOG */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">7. Przekazywanie Danych Poza EOG</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>W związku z korzystaniem z usług dostawców AI (OpenAI, Anthropic, Google), dane mogą być przekazywane do Stanów Zjednoczonych.</li>
                <li>Przekazywanie odbywa się na podstawie standardowych klauzul umownych zatwierdzonych przez Komisję Europejską lub decyzji o adekwatności.</li>
                <li>Administrator zapewnia, że przekazywanie danych odbywa się z zachowaniem odpowiedniego poziomu ochrony.</li>
              </ol>
            </section>

            {/* Prawa użytkownika */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">8. Prawa Użytkownika</h2>
              <p className="mb-4">Użytkownikowi przysługują następujące prawa:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Prawo dostępu</strong> – prawo do uzyskania informacji o przetwarzanych danych oraz otrzymania ich kopii.</li>
                <li><strong>Prawo do sprostowania</strong> – prawo do żądania poprawienia nieprawidłowych danych.</li>
                <li><strong>Prawo do usunięcia („prawo do bycia zapomnianym")</strong> – prawo do żądania usunięcia danych w określonych przypadkach.</li>
                <li><strong>Prawo do ograniczenia przetwarzania</strong> – prawo do żądania ograniczenia przetwarzania danych.</li>
                <li><strong>Prawo do przenoszenia danych</strong> – prawo do otrzymania danych w ustrukturyzowanym formacie.</li>
                <li><strong>Prawo do sprzeciwu</strong> – prawo do wniesienia sprzeciwu wobec przetwarzania danych.</li>
                <li><strong>Prawo do wycofania zgody</strong> – w przypadku przetwarzania na podstawie zgody, prawo do jej wycofania w dowolnym momencie.</li>
                <li><strong>Prawo do skargi</strong> – prawo do wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.</li>
              </ol>
              <p className="mt-4">W celu realizacji powyższych praw należy skontaktować się z Administratorem: <strong>crm@kurdynowski.pl</strong></p>
            </section>

            {/* Bezpieczeństwo */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">9. Bezpieczeństwo Danych</h2>
              <p className="mb-4">Administrator stosuje odpowiednie środki techniczne i organizacyjne zapewniające ochronę danych osobowych, w tym:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>szyfrowanie połączeń (protokół HTTPS/SSL),</li>
                <li>szyfrowanie haseł (algorytm bcrypt),</li>
                <li>regularne kopie zapasowe,</li>
                <li>kontrola dostępu do systemów,</li>
                <li>monitorowanie bezpieczeństwa.</li>
              </ul>
            </section>

            {/* Pliki cookies */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">10. Pliki Cookies</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Serwis wykorzystuje pliki cookies (ciasteczka) w celu:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>utrzymania sesji Użytkownika,</li>
                    <li>zapamiętania preferencji,</li>
                    <li>analityki i statystyk.</li>
                  </ul>
                </li>
                <li>Użytkownik może zarządzać plikami cookies poprzez ustawienia przeglądarki.</li>
                <li>Wyłączenie niektórych cookies może ograniczyć funkcjonalność Serwisu.</li>
              </ol>
            </section>

            {/* Zmiany */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">11. Zmiany Polityki Prywatności</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Administrator zastrzega sobie prawo do zmiany niniejszej Polityki Prywatności.</li>
                <li>O istotnych zmianach Użytkownicy zostaną powiadomieni drogą mailową lub poprzez komunikat w Serwisie.</li>
                <li>Aktualna wersja Polityki Prywatności jest zawsze dostępna w Serwisie.</li>
              </ol>
            </section>

            {/* Kontakt */}
            <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="font-heading text-lg text-primary-900 dark:text-slate-100 mb-2">Kontakt w Sprawach Ochrony Danych</h3>
              <p className="text-slate-700">
                <strong>Administrator danych:</strong> Biuro Rachunkowe Kurdynowski<br />
                <strong>Adres:</strong> ul. Kościuszki 27, 85-079 Bydgoszcz<br />
                <strong>NIP:</strong> 5541008895<br />
                <strong>E-mail:</strong> crm@kurdynowski.pl<br />
                <strong>Serwis:</strong> KAI (Kurdynowski AI)
              </p>
              <p className="text-slate-600 dark:text-slate-400 mt-4 text-xs">
                <strong>Organ nadzorczy:</strong><br />
                Prezes Urzędu Ochrony Danych Osobowych<br />
                ul. Stawki 2, 00-193 Warszawa<br />
                www.uodo.gov.pl
              </p>
            </div>

            <p className="text-muted-foreground text-center mt-8">
              © 2026 KAI - Kurdynowski AI. Wszelkie prawa zastrzeżone.<br />
              Administrator danych: Biuro Rachunkowe Kurdynowski, ul. Kościuszki 27, 85-079 Bydgoszcz, NIP: 5541008895
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
